import React, { useCallback } from 'react';
import classNames from 'classnames';
import { useModal } from 'hooks/useModal';
import { GeneralModal } from 'ui/GeneralModal';
import { useCurrentWidth } from 'effects';
import { SvgIcon } from 'ui/SvgIcon';
import FilmLine from 'ui/Icons/film-line.component.svg';
import PlayCircleOutlineIcon from 'ui/Icons/play-circle-outline.component.svg';
import { IMediaGalleryProps, IMediaImageUpload } from './types';
import { MediaCarouselModal } from './MediaCarouselModal';

interface IMediaImageProps {
  upload: IMediaImageUpload;
  index: number;
  className?: string;
  imgClassName?: string;
  onClick?: (uploadIndex: number) => void;
  overlayText?: string;
  baseUrlPath: string;
}

const MediaImage = (props: IMediaImageProps) => {
  // Create srcset string for responsive images
  const srcset = Object.entries(props.upload.sizes)
    .map(([size, details]) => `${props.baseUrlPath}${details.path} ${details.width}w`)
    .join(', ');

  // Use the small size as the default src
  const defaultSrc = props.upload.isVimeoThumbnail
    ? props.upload.sizes.s.path
    : `${props.baseUrlPath}${props.upload.sizes.s.path}`;

  const sizes = [
    `(max-width: 640px) ${props.upload.sizes.s.width}px`,
    `(max-width: 768px) ${props.upload.sizes.m.width}px`,
    `(max-width: 1024px) ${props.upload.sizes.l.width}px`,
    `(max-width: 1279px) ${props.upload.sizes.xl.width}px`,
  ].join(', ');

  return (
    <div
      onClick={() => {
        props.onClick && props.onClick(props.index);
      }}
      className={classNames(
        'relative bg-cover bg-center',
        {
          'cursor-pointer': props.onClick,
        },
        props.className
      )}
    >
      <img
        style={{
          backgroundImage: `url('${defaultSrc}')`,
        }}
        src={defaultSrc}
        srcSet={props.upload.isVimeoThumbnail ? undefined : srcset}
        sizes={props.upload.isVimeoThumbnail ? undefined : sizes}
        alt={props.upload.displayName}
        className={classNames('object-cover w-full h-full bg-cover bg-center', props.imgClassName)}
        loading="lazy"
      />

      {props.upload.isVimeoThumbnail && (
        <>
          <div className="absolute inset-0 bg-black/40" />
          <SvgIcon
            IconComponent={PlayCircleOutlineIcon}
            className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] fill-white w-[48px] h-[48px]"
          />
        </>
      )}

      {props.overlayText && (
        <span className="bg-black/50 w-full h-full absolute top-0 left-0 right-0 bottom-0">
          <span className="text-white font-hurmegeometric-sans text-14px underline absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
            {props.overlayText}
          </span>
        </span>
      )}
    </div>
  );
};

const VimeoLinkAndRenderer = ({ vimeoVideoId }: { vimeoVideoId: string }) => {
  const videoPlayerModal = useModal();
  return (
    <>
      <div className="bg-brown-10 border border-solid border-brown-20 px-5px py-8px mt-10px">
        <button
          className="bg-transparent hover:bg-gray-20 cursor-pointer"
          onClick={() => {
            videoPlayerModal.showModal();
          }}
        >
          <span className="flex items-center gap-2">
            <SvgIcon IconComponent={FilmLine} className="fill-brown-prime stroke-none" />
            <span className="font-hurmegeometric-sans text-13px text-brown-prime underline">Video link</span>
          </span>
        </button>
      </div>

      <GeneralModal
        isOpen={videoPlayerModal.isOpen}
        onClose={videoPlayerModal.handleClose}
        shouldCloseByClickingOutside={true}
        modalWindowClassName={`w-full
mx-2
h-[500px]
p-8
sm:pt-[30px]
md:mx-0
md:w-[600px]
lg:pt-[60px]
lg:w-[800px]`}
      >
        <iframe
          src={`https://player.vimeo.com/video/${vimeoVideoId}`}
          width="100%"
          height="100%"
          allow="autoplay; fullscreen;"
        ></iframe>
      </GeneralModal>
    </>
  );
};

export const MediaGallery = (props: IMediaGalleryProps) => {
  const { vimeoVideoId, uploads, titleForModal, baseUrlPath, hideVimeoLink, enableBulkDownload } = props;
  const currentWidths = useCurrentWidth();
  const modalOps = useModal<{
    index: number;
  }>();
  const imageUploads = uploads.filter(u => u.fileType === 'image');

  const handleOnClickImage = useCallback(
    async (uploadIndex: number) => {
      await modalOps.showModal({
        index: uploadIndex,
      });
    },
    [modalOps]
  );

  return (
    <>
      {imageUploads.length === 1 && (
        <MediaImage
          index={0}
          onClick={handleOnClickImage}
          className="w-full h-[260px]"
          upload={imageUploads[0]}
          baseUrlPath={baseUrlPath}
        />
      )}

      {imageUploads.length === 2 && (
        <div className="flex items-center gap-2">
          <MediaImage
            index={0}
            onClick={handleOnClickImage}
            className="w-2/3 h-[260px]"
            upload={imageUploads[0]}
            baseUrlPath={baseUrlPath}
          />
          <MediaImage
            index={1}
            onClick={handleOnClickImage}
            className="w-1/3 h-[260px]"
            upload={imageUploads[1]}
            baseUrlPath={baseUrlPath}
          />
        </div>
      )}

      {imageUploads.length === 3 && (
        <div className="flex items-center gap-2">
          <MediaImage
            index={0}
            onClick={handleOnClickImage}
            className="w-2/3 h-[528px]"
            upload={imageUploads[0]}
            baseUrlPath={baseUrlPath}
          />
          <div className="flex flex-col items-center justify-center gap-2 w-1/3 h-full">
            <MediaImage
              index={1}
              onClick={handleOnClickImage}
              className="w-full h-[260px]"
              upload={imageUploads[1]}
              baseUrlPath={baseUrlPath}
            />
            <MediaImage
              index={2}
              onClick={handleOnClickImage}
              className="w-full h-[260px]"
              upload={imageUploads[2]}
              baseUrlPath={baseUrlPath}
            />
          </div>
        </div>
      )}

      {imageUploads.length >= 4 && (
        <div className="flex items-center gap-2">
          <MediaImage
            index={0}
            onClick={handleOnClickImage}
            className="h-[260px] hidden sm:block sm:1/3 lg:w-3/5"
            upload={imageUploads[0]}
            baseUrlPath={baseUrlPath}
          />
          <MediaImage
            index={currentWidths.isXs ? 0 : 1}
            onClick={handleOnClickImage}
            className="h-[260px] w-1/2 sm:w-1/3 lg:1/5"
            upload={currentWidths.isXs ? imageUploads[0] : imageUploads[1]}
            baseUrlPath={baseUrlPath}
          />

          <div className="flex flex-col items-center justify-center gap-2 h-full w-1/2 sm:1/3 lg:w-1/5">
            <MediaImage
              index={currentWidths.isXs ? 1 : 2}
              onClick={handleOnClickImage}
              className="w-full h-[126px]"
              upload={currentWidths.isXs ? imageUploads[1] : imageUploads[2]}
              baseUrlPath={baseUrlPath}
            />
            <MediaImage
              index={currentWidths.isXs ? 2 : 3}
              onClick={handleOnClickImage}
              className="w-full h-[126px]"
              upload={currentWidths.isXs ? imageUploads[2] : imageUploads[3]}
              overlayText={imageUploads.length > 4 ? `+${imageUploads.length - 4} photos` : undefined}
              baseUrlPath={baseUrlPath}
            />
          </div>
        </div>
      )}

      {vimeoVideoId && !hideVimeoLink && <VimeoLinkAndRenderer vimeoVideoId={vimeoVideoId} />}

      <MediaCarouselModal
        isOpen={modalOps.isOpen}
        onClose={modalOps.handleClose}
        titleForModal={titleForModal}
        images={imageUploads.map(u => {
          return {
            displayName: u.displayName,
            url: u.isVimeoThumbnail ? u.sizes.original.path : `${baseUrlPath}${u.sizes.original.path}`,
            vimeoVideoId: u.isVimeoThumbnail ? vimeoVideoId : undefined,
            uuid: u.uuid,
          };
        })}
        initialSlide={modalOps.modalData?.index || 0}
        enableBulkDownload={enableBulkDownload}
      />
    </>
  );
};
